/* eslint react-hooks/exhaustive-deps: error */
import * as React from 'react';
import {emotionStyled} from '@packages/utilities/styled';
import {
  Button,
  ChangeMetaType,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  PreviewSelect,
  Spacer,
  Text,
  View,
  VStack,
  WarningTriangleIcon,
} from '@packages/systems/spring';
import {
  type VariableCollection,
  type VariableModeId,
  BASE_VARIABLE_MODE_ID,
  INHERIT_VARIABLE_MODE_ID,
} from '@packages/systems/core/utils/StyleVariables';
import {getVariableModeEntries} from './getVariableModeEntries';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {capitalize} from '@packages/utilities/string-utils';
import {useDispatch} from '@packages/systems/designer/flux';
import {ActionCreator, createAction} from '@packages/utilities/create-action';
import {STYLE_VARIABLE_MODE_RESTORED} from '@packages/systems/designer/actions';

export const styleVariableModeRestored: ActionCreator<
  typeof STYLE_VARIABLE_MODE_RESTORED,
  {
    /** The id of the variable mode to restore. */
    id: VariableModeId;
    /** The id of the variable collection to restore the mode to. */
    collectionId: VariableCollectionId;
  }
> = createAction(STYLE_VARIABLE_MODE_RESTORED);

const WarningButton = emotionStyled(Button)({
  display: 'grid',
  width: '100%',
  gridAutoFlow: 'column',
  justifyContent: 'start',
  gap: '$1',
  background: '$yellowTransparent !important',
  color: '$yellowText !important',
  '&:focus-visible': {
    outline: 'none',
  },
  '&:hover': {
    color: '$yellowText !important',
    background: ' $yellowTransparentHover !important ',
    '& svg': {
      color: '$yellowText !important',
    },
  },
});

const DeletedVariableModePopover = React.memo<{
  selectedLabel: string;
  isRestorableMode: boolean;
  onUnlink: () => void;
  onRestore: () => void;
}>(({selectedLabel, isRestorableMode, onUnlink, onRestore}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const closePopover = React.useCallback(() => setIsOpen(false), []);
  const togglePopover = React.useCallback(
    () => setIsOpen((current) => !current),
    []
  );

  return (
    <Popover shouldShowMask isOpen={isOpen} onOpenChange={togglePopover}>
      <PopoverTrigger>
        <WarningButton onClick={togglePopover}>
          <View
            display="grid"
            alignItems="center"
            gap="$2"
            gridAutoFlow="column"
          >
            <WarningTriangleIcon aria-label="Warning" colorVariant="warning" />
            <Text truncateToLines={1}>{selectedLabel} (deleted)</Text>
          </View>
        </WarningButton>
      </PopoverTrigger>
      <PopoverContent aria-label="Restore deleted mode popover content">
        <PopoverHeader title="Deleted variable mode" />
        <PopoverBody padding="$2">
          <VStack>
            <Text colorVariant="secondary">
              Mode <Text colorVariant="primary">{selectedLabel}</Text> was
              deleted, but its value is still being used here.
            </Text>
            <Text colorVariant="secondary">
              {capitalize(
                `${isRestorableMode ? 'you can restore this mode, or ' : ''}you can unlink to choose a different value.`
              )}
            </Text>
          </VStack>
        </PopoverBody>
        <PopoverFooter>
          <HStack>
            {isRestorableMode && (
              <Button variant="outline" onClick={onRestore}>
                Restore
              </Button>
            )}
            <Spacer />
            <Button onClick={closePopover}>Cancel</Button>
            <Button colorVariant="primary" onClick={onUnlink}>
              Unlink
            </Button>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
});

export const VariableModeSelect = React.memo<{
  collection: VariableCollection;
  value: VariableModeId;
  onChange: (
    value: VariableModeId,
    meta: {
      option: {
        value: string;
        label: string;
      };
      changeType: ChangeMetaType;
    }
  ) => void;
  onPreselect?: (
    value: VariableModeId,
    meta: {
      option: {
        value: string;
        label: string;
      };
      changeType: ChangeMetaType;
    }
  ) => void;
  onUnlink: () => void;
  isDisabled?: boolean;
  showInherit?: boolean;
  baseDisplayName?: string;
  visibleModeIds?: string[];
}>(
  ({
    collection,
    value,
    onChange,
    onPreselect,
    onUnlink,
    isDisabled,
    showInherit,
    baseDisplayName,
    visibleModeIds,
  }) => {
    const dispatch = useDispatch();
    const isInheritVariableModesEnabled = useFeatureFlag(
      FEATURE_FLAGS.CAMPAIGN_BUILD_MODE
    );
    const shouldShowInherit =
      isInheritVariableModesEnabled && (showInherit ?? true);
    const baseLabel = baseDisplayName || 'Base';

    // Track the committed value (updated on actual selection and
    // external changes like undo, but not during hover previews)
    // so the disabled stale-value entry stays stable.
    const [committedValue, setCommittedValue] = React.useState(value);
    const isPreselectingRef = React.useRef(false);

    // Sync committedValue when value changes externally (e.g. undo)
    // but not during hover previews.
    React.useEffect(() => {
      if (!isPreselectingRef.current) {
        setCommittedValue(value);
      }
    }, [value]);

    const filteredOptions = React.useMemo(
      () =>
        getVariableModeEntries(collection, {
          showInherit: shouldShowInherit,
          baseDisplayName: baseLabel,
          visibleModeIds,
        }).map(({id, name}) => ({value: id, label: name})),
      [collection, shouldShowInherit, baseLabel, visibleModeIds]
    );

    // If the committed value isn't in the filtered options, include it
    // as a disabled entry so the Select displays it but the user can't
    // re-select it.
    const options = React.useMemo(() => {
      if (
        !committedValue ||
        filteredOptions.some((opt) => opt.value === committedValue)
      ) {
        return filteredOptions;
      }

      let currentLabel: string;
      if (committedValue === INHERIT_VARIABLE_MODE_ID) {
        currentLabel = 'Inherit';
      } else if (committedValue === BASE_VARIABLE_MODE_ID) {
        currentLabel = baseLabel;
      } else {
        const m = collection.modes[committedValue];
        currentLabel = m?.name ?? committedValue;
      }

      return [
        {value: committedValue, label: currentLabel, isDisabled: true},
        ...filteredOptions,
      ];
    }, [filteredOptions, committedValue, collection.modes, baseLabel]);

    const mode = collection.modes[value];
    const isSelectedModeDeleted = collection.deleted || mode?.deleted;

    const isLibraryCollection = Boolean(collection.origin);
    const isRestorableMode = !isLibraryCollection;

    const handleRestore = React.useCallback(() => {
      if (mode == null || isLibraryCollection) return;

      dispatch(
        styleVariableModeRestored({
          id: value,
          collectionId: collection.id,
        })
      );
    }, [collection.id, dispatch, mode, value, isLibraryCollection]);

    // PreviewSelect exposes a single onChange that emits every lifecycle
    // event. Route changeEnd to the real onChange (commit) and everything
    // else to onPreselect (hover previews), while also keeping
    // isPreselectingRef and committedValue in sync for options stability.
    const handlePreviewChange = React.useCallback(
      (
        nextValue: VariableModeId,
        meta: {
          option: {value: string; label: string};
          changeType: ChangeMetaType;
        }
      ) => {
        if (meta.changeType === 'changeStart') {
          isPreselectingRef.current = true;
          onPreselect?.(nextValue, meta);
          return;
        }

        if (meta.changeType === 'changeEnd') {
          isPreselectingRef.current = false;
          setCommittedValue(nextValue);
          onChange(nextValue, meta);
          return;
        }

        if (meta.changeType === 'changeCancel') {
          isPreselectingRef.current = false;
          onPreselect?.(nextValue, meta);
          return;
        }

        onPreselect?.(nextValue, meta);
      },
      [onChange, onPreselect]
    );

    const selectedLabel = mode?.name || 'Error: Unknown mode';

    if (isSelectedModeDeleted) {
      return (
        <DeletedVariableModePopover
          selectedLabel={selectedLabel}
          isRestorableMode={isRestorableMode}
          onUnlink={onUnlink}
          onRestore={handleRestore}
        />
      );
    }

    // Remount Select when the stable option set changes so it
    // recalculates dropdown height. Derived from options (which
    // only change on author config or committed value changes,
    // not during hover previews).
    const optionsKey = options.map((o) => o.value).join(',');

    return (
      <PreviewSelect
        key={optionsKey}
        options={options}
        value={value}
        onChange={handlePreviewChange}
        isDisabled={isDisabled}
        aria-label="Select a variable mode"
        shouldTruncateButtonText
      />
    );
  }
);
